/**
 * Because this application relies heavily on the system of action and request classes
 * (as opposed to more standard in Redux simple objects),
 * we'd like to provide several helper saga effects for 'take'-ing and 'put'-ing actions.
 *
 * Also this file exports some unmodified effects and types for convenient import.
 */

import {
    put as basePut, take as baseTake,
    takeEvery as baseTakeEvery, takeLatest as baseTakeLatest
} from 'redux-saga/effects';
import { ForkEffect, PutEffect, TakeEffect } from 'redux-saga/effects';
import type { Effect } from 'redux-saga/effects';
import { Action as BaseReduxAction } from 'redux';
import {
    Action,
    ActionClass,
    actionMatches,
    FetchClass,
    FetchError,
    FetchFatalError,
    FetchSuccess,
    getFetchActionTypeForRequestClass,
    ReduxAction
} from 'acscp/actions';
import { BaseRequest, RequestClass } from 'acscp/api/requests';
import { ResponseErrorTypes, ResponseType } from 'acscp/api/responses';
import { ErrorDetails } from 'acscp/api/models/errorDetails';
import { ErrorMessage } from 'acscp/api/models/errorMessage';

/**
 * All take-like effects (take, takeEvery, takeLatest) normally rely on action type property
 * to select the desired actions.
 * Since we can't easily use this system we rely on the action matcher overload of these effects.
 * Actions matcher is a function that is run against _every_ Redux action
 * to determine if the handler should be run against it.
 *
 * In our case, we use some helper functions to compare the actual Redux action
 * and the specified action or request class.
 */

type ActionMatcher = (action: BaseReduxAction) => boolean;

export function actionMatcher(actionDescriptor: ActionClass<any> | string): ActionMatcher {
    return action => actionMatches(actionDescriptor, action);
}

export const successfulRequestActionMatcher = requestActionMatcherBuilder(FetchSuccess);
export const errorRequestActionMatcher = requestActionMatcherBuilder(FetchError);
export const fatalErrorRequestActionMatcher = requestActionMatcherBuilder(FetchFatalError);

type RequestActionMatcherBuilder = (requestClass: RequestClass<any>) => ActionMatcher;

export function requestActionMatcherBuilder(fetchActionClass: FetchClass): RequestActionMatcherBuilder {

    return requestClass => {
        const actionType = getFetchActionTypeForRequestClass(fetchActionClass, requestClass);

        return actionMatcher(actionType);
    };
}

// 'take' effects

export const take =
    (actionClass: ActionClass<any>) => baseTake(actionMatcher(actionClass));

export const takeSuccessfulRequest =
    (requestClass: RequestClass<any>) => baseTake(successfulRequestActionMatcher(requestClass));

export const takeErrorRequest =
    (requestClass: RequestClass<any>) => baseTake(errorRequestActionMatcher(requestClass));

export const takeFatalErrorRequest =
    (requestClass: RequestClass<any>) => baseTake(fatalErrorRequestActionMatcher(requestClass));

// 'takeEvery' effects

export function takeEvery<A extends Action>(
    actionClass: ActionClass<A>,
    worker: (action: A) => any
): ForkEffect {

    return baseTakeEvery(actionMatcher(actionClass),
        (reduxAction: ReduxAction<A>) => worker(reduxAction.payload));
}

export function takeEveryCompletedRequest<TPayload, TRequest extends BaseRequest<TPayload>>(
    requestClass: RequestClass<TRequest>,
    worker: (payload: TPayload, request: TRequest) => any
): ForkEffect {

    return baseTakeEvery(successfulRequestActionMatcher(requestClass),
        (reduxAction: ReduxAction<FetchSuccess<TPayload, TRequest>>) =>
            worker(reduxAction.payload.response, reduxAction.payload.request)
    );
}

export function takeEveryErrorRequest<TRequest extends BaseRequest<any>>(
    requestClass: RequestClass<TRequest>,
    worker: (responseType: ResponseErrorTypes, error: ErrorDetails, request: TRequest) => any
): ForkEffect {

    return baseTakeEvery(errorRequestActionMatcher(requestClass),
        (reduxAction: ReduxAction<FetchError<TRequest>>) =>
            worker(reduxAction.payload.responseType, reduxAction.payload.error, reduxAction.payload.request)
    );
}

export function takeEveryFatalErrorRequest(
    requestClass: RequestClass<any>,
    worker: (message: ErrorMessage, request: BaseRequest<any>) => any
): ForkEffect {

    return baseTakeEvery(fatalErrorRequestActionMatcher(requestClass),
        (reduxAction: ReduxAction<FetchFatalError>) =>
            worker(reduxAction.payload.message, reduxAction.payload.request)
    );
}

// Other effects

export function takeLatest<A extends Action>(actionClass: ActionClass<A>, worker: (action: A) => any): ForkEffect {
    return baseTakeLatest(
        actionMatcher(actionClass),
        (reduxAction: ReduxAction<A>) => worker(reduxAction.payload));
}

export function put<A extends Action>(action: A): PutEffect<ReduxAction<A>> {
    return basePut(action.makeReduxAction());
}

/**
 * Type that all redux-saga generator functions should return.
 * Note that currently types of values _returned_ by the generator functions are ignored by TS typing system.
 */
export type SagaIterator = IterableIterator<Effect>;

// Export unmodified effects, so all of them can be imported from this file
export type { Effect } from 'redux-saga/effects';
export { all, apply, call, fork, race, select } from 'redux-saga/effects';
